/*
 * DSD Identity Registry - p11dz148
 * Identity-only authority contract for Shopify/Firebase/profile/user identity.
 * Defines canonical identity shape, username/handle doctrine, verification/trust
 * state placeholders, account state placeholders, and governance assignment
 * identity dependencies. Does not change login, profile rendering, Firebase
 * reads/writes, realtime listeners, profile persistence, moderation enforcement,
 * membership enforcement, or user-visible UI.
 */
(function(){
  'use strict';
  if (window.DSDIdentityRegistry && window.DSDIdentityRegistry.version) return;

  var VERSION = 'p11dz148-identity-registry-v1';
  var OWNER = 'IdentityRegistry';
  var FIREBASE_GATE = 'disabled-identity-contract-only';

  function claim(surface, meta){
    try {
      if (window.DSDRegistry && typeof window.DSDRegistry.claim === 'function') {
        window.DSDRegistry.claim(surface, OWNER, Object.assign({ version: VERSION }, meta || {}));
      }
    } catch(e) {}
  }

  function exists(name){ return !!window[name]; }
  function slug(value){
    return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-+|-+$/g,'');
  }
  function handle(value){
    return String(value || '').trim().toLowerCase().replace(/^@+/,'').replace(/[^a-z0-9_.-]+/g,'').slice(0,32);
  }
  function nowIso(){ try { return new Date().toISOString(); } catch(e) { return ''; } }

  var identityTypes = {
    shopifyCustomer: { key: 'shopifyCustomer', label: 'Shopify Customer', source: 'Shopify' },
    firebaseUser: { key: 'firebaseUser', label: 'Firebase User', source: 'Firebase' },
    profile: { key: 'profile', label: 'Member Profile', source: 'ProfileRenderer' },
    creator: { key: 'creator', label: 'Creator Identity', source: 'Future CreatorRegistry' },
    moderator: { key: 'moderator', label: 'Moderator Identity', source: 'Future GovernanceRegistry' },
    admin: { key: 'admin', label: 'Admin Identity', source: 'Future GovernanceRegistry' }
  };

  var accountStates = {
    active: { key: 'active', allowedToPost: true, allowedToInteract: true },
    pending: { key: 'pending', allowedToPost: false, allowedToInteract: false },
    restricted: { key: 'restricted', allowedToPost: false, allowedToInteract: true },
    muted: { key: 'muted', allowedToPost: false, allowedToInteract: false },
    suspended: { key: 'suspended', allowedToPost: false, allowedToInteract: false },
    banned: { key: 'banned', allowedToPost: false, allowedToInteract: false },
    deleted: { key: 'deleted', allowedToPost: false, allowedToInteract: false }
  };

  var verificationStates = {
    unverified: { key: 'unverified', rank: 0 },
    emailVerified: { key: 'emailVerified', rank: 10 },
    trusted: { key: 'trusted', rank: 30 },
    creatorVerified: { key: 'creatorVerified', rank: 50 },
    moderatorVerified: { key: 'moderatorVerified', rank: 70 },
    adminVerified: { key: 'adminVerified', rank: 100 }
  };

  var identityDoctrine = [
    'IdentityRegistry is the canonical identity contract, not a login replacement.',
    'Shopify customer identity, Firebase auth identity, and DSD profile identity must map into one canonical identity record before write activation.',
    'Profiles may display identity data, but profiles do not own identity authority.',
    'Governance, membership, moderation, notifications, stories, universes, galaxies, and galaxy chat must reference canonical userId/profileKey relationships instead of inventing independent identity keys.',
    'Username and handle changes require future cooldown, reserved-name, impersonation, and audit-log policies before enforcement.',
    'Account state and verification state are metadata contracts only until TrustRegistry and ModerationRegistry are activated.',
    'This registry does not enable Firebase reads, Firebase writes, realtime listeners, login changes, profile persistence changes, moderation actions, or access enforcement.'
  ];

  var reservedHandlePolicy = {
    futureRequired: true,
    examples: ['admin','support','moderator','system','staff','official','help','security'],
    activationGate: 'Requires TrustRegistry + ModerationRegistry + AuditLogRegistry before enforcement.'
  };

  var futurePathContracts = {
    identities: 'identities/{userId}',
    userIdentity: 'users/{userId}/identity/current',
    profileIdentityIndex: 'profileIdentityIndex/{profileKey}',
    usernameIndex: 'usernameIndex/{normalizedHandle}',
    identityAudit: 'auditLogs/identity/{userId}/items/{auditId}',
    verification: 'identities/{userId}/verification/current',
    accountState: 'identities/{userId}/accountState/current'
  };

  function normalizeIdentity(input, context){
    input = input || {};
    context = context || {};
    var profileKey = input.profileKey || input.profileHandle || context.profileKey || null;
    var userId = input.userId || input.uid || input.firebaseUid || context.userId || null;
    var displayName = input.displayName || input.name || input.username || context.displayName || 'Member';
    var normalizedHandle = handle(input.handle || input.username || profileKey || displayName);
    if (!profileKey && normalizedHandle) profileKey = normalizedHandle;
    return {
      version: VERSION,
      userId: userId,
      profileKey: profileKey,
      shopifyCustomerId: null,
      firebaseUid: input.firebaseUid || userId || null,
      email: null,
      displayName: displayName,
      handle: normalizedHandle,
      accountState: accountStates[input.accountState] ? input.accountState : 'active',
      verificationState: verificationStates[input.verificationState] ? input.verificationState : 'unverified',
      trustState: input.trustState || 'unscored',
      membershipState: input.membershipState || 'unknown',
      createdAt: input.createdAt || context.createdAt || null,
      updatedAt: nowIso(),
      firebaseReadsEnabled: false,
      firebaseWritesEnabled: false,
      realtimeEnabled: false,
      enforcementMode: 'identity-contract-only'
    };
  }

  function getCurrentIdentitySnapshot(){
    var auth = window.DSDAuthState || window.DSDFirebaseAuth || {};
    var provider = window.DSDLaunchSocial || {};
    var profile = null;
    try {
      if (provider && typeof provider.getCurrentProfile === 'function') profile = provider.getCurrentProfile();
      if (!profile && provider && typeof provider.getProfile === 'function') profile = provider.getProfile();
    } catch(e) {}
    profile = profile || {};
    var authUser = auth.currentUser || auth.user || auth.customer || {};
    return normalizeIdentity({
      userId: authUser.uid || authUser.id || auth.uid || null,
      firebaseUid: authUser.uid || auth.uid || null,
      shopifyCustomerId: null,
      email: null,
      profileKey: profile.profileKey || profile.key || profile.handle || null,
      displayName: profile.displayName || profile.name || authUser.displayName || null,
      handle: profile.handle || profile.username || null,
      accountState: profile.accountState || 'active',
      verificationState: profile.verificationState || 'unverified',
      trustState: profile.trustState || 'unscored',
      membershipState: profile.membershipState || 'unknown'
    });
  }

  function getReport(reason){
    return {
      version: VERSION,
      reason: reason || 'manual-report',
      owner: OWNER,
      firebaseGate: FIREBASE_GATE,
      firebaseReadsEnabled: false,
      firebaseWritesEnabled: false,
      realtimeEnabled: false,
      loginBehaviorChanged: false,
      profileRenderingChanged: false,
      enforcementEnabled: false,
      doctrine: identityDoctrine,
      identityTypes: identityTypes,
      accountStates: accountStates,
      verificationStates: verificationStates,
      reservedHandlePolicy: reservedHandlePolicy,
      futurePathContracts: futurePathContracts,
      currentIdentitySnapshot: getCurrentIdentitySnapshot(),
      dependencyReadiness: {
        DSDRegistry: exists('DSDRegistry'),
        DSDIdentityAuthority: exists('DSDIdentityAuthority'),
        DSDFirebaseAuth: exists('DSDFirebaseAuth'),
        DSDAuthState: exists('DSDAuthState'),
        DSDLaunchSocial: exists('DSDLaunchSocial'),
        DSDMembershipRegistry: exists('DSDMembershipRegistry'),
        DSDGovernanceRegistry: exists('DSDGovernanceRegistry'),
        DSDModerationMetadata: exists('DSDModerationMetadata'),
        firebaseReadsEnabled: false,
        firebaseWritesEnabled: false,
        realtimeEnabled: false
      },
      nextGate: 'p11dz149-trust-reputation-foundation-or-controlled-read-only-post-activation-gate'
    };
  }

  function publish(reason){
    var report = getReport(reason || 'publish');
    claim('identity-registry:doctrine', { doctrine: identityDoctrine, count: identityDoctrine.length });
    claim('identity-registry:identity-types', { identityTypes: identityTypes, count: Object.keys(identityTypes).length });
    claim('identity-registry:account-states', { accountStates: accountStates, count: Object.keys(accountStates).length });
    claim('identity-registry:verification-states', { verificationStates: verificationStates, count: Object.keys(verificationStates).length });
    claim('identity-registry:reserved-handle-policy', reservedHandlePolicy);
    claim('identity-registry:path-contracts', futurePathContracts);
    claim('identity-registry:current-identity-snapshot', report.currentIdentitySnapshot);
    claim('identity-registry:dependency-readiness', report.dependencyReadiness);
    claim('identity-registry:firebase-firewall', {
      firebaseReadsEnabled: false,
      firebaseWritesEnabled: false,
      realtimeEnabled: false,
      loginBehaviorChanged: false,
      profileRenderingChanged: false,
      enforcementEnabled: false,
      gate: FIREBASE_GATE
    });
    try { window.dispatchEvent(new CustomEvent('dsd:identity-registry-ready', { detail: report })); } catch(e) {}
    window.DSDIdentityRegistryReport = report;
    return report;
  }

  window.DSDIdentityRegistry = {
    version: VERSION,
    owner: OWNER,
    getReport: getReport,
    publish: publish,
    normalizeIdentity: normalizeIdentity,
    getCurrentIdentitySnapshot: getCurrentIdentitySnapshot,
    identityTypes: identityTypes,
    accountStates: accountStates,
    verificationStates: verificationStates,
    futurePathContracts: futurePathContracts
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function(){ publish('dom-ready'); }, { once: true });
  } else {
    publish('already-ready');
  }
})();
